import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { reviewCenterService } from '../../services/review-center.service';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import type {
  ReviewCenterData,
  QueueItem,
  AreaState,
  Severity,
} from '../../types/review-center.types';
import { PageSkeleton } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';

// ---------------------------------------------------------------------------
// Small presentation helpers
// ---------------------------------------------------------------------------

const AREA_DOT: Record<AreaState, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

const SEV_BAR: Record<Severity, string> = {
  critical: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-sky-500',
};

const SYSTEM_HEALTH_TONE: Record<string, string> = {
  GOOD: 'text-emerald-600 dark:text-emerald-400',
  FAIR: 'text-amber-600 dark:text-amber-400',
  'AT RISK': 'text-red-600 dark:text-red-400',
};

const DAY_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

const ACTION_LABEL: Record<QueueItem['action'], string> = {
  acknowledge: 'Acknowledge',
  review: 'Review',
  fix: 'Fix',
  retry: 'Retry',
  resend: 'Resend',
};

const ageLabel = (m: number | null): string => {
  if (m === null || m === undefined) return '';
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h`;
  return `${Math.floor(m / 1440)}d`;
};

const relative = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

// Where a row drills through when its action isn't an inline mutation.
const routeFor = (row: QueueItem): string => {
  switch (row.action) {
    case 'acknowledge':
      return '/error-logs';
    case 'retry':
      return '/background-jobs';
    case 'fix':
      return '/access-control';
    case 'review':
      return row.stream === 'AI Quality' ? '/evals' : '/feedback';
    default:
      return '/notification-runs';
  }
};

// A tiny inline sparkline (no chart dependency). Inherits colour via currentColor.
const Sparkline = ({ data }: { data: number[] }) => {
  if (!data || data.length < 2) {
    return <div className="h-8 flex items-center text-[11px] text-gray-400">—</div>;
  }
  const w = 140;
  const h = 32;
  const pad = 2;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = (w - pad * 2) / (data.length - 1);
  const pts = data
    .map((v, i) => {
      const x = pad + i * step;
      const y = h - pad - ((v - min) / range) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} className="w-full">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth={1.5} />
    </svg>
  );
};

// Compare the recent half of a series to the earlier half → % change + direction.
const trendDelta = (arr: number[]): { pct: number | null; dir: 'up' | 'down' | 'flat' } => {
  if (!arr || arr.length < 2) return { pct: null, dir: 'flat' };
  const half = Math.max(1, Math.floor(arr.length / 2));
  const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
  const e = avg(arr.slice(0, half));
  const r = avg(arr.slice(-half));
  if (e === 0) return { pct: r === 0 ? 0 : null, dir: r > 0 ? 'up' : 'flat' };
  const pct = Math.round(((r - e) / Math.abs(e)) * 100);
  return { pct, dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
};

const TrendTile = ({
  title,
  data,
  goodDir,
}: {
  title: string;
  data: number[];
  goodDir: 'up' | 'down' | null;
}) => {
  const { pct, dir } = trendDelta(data);
  const good = goodDir !== null && dir === goodDir;
  const bad = goodDir !== null && dir !== 'flat' && dir !== goodDir;
  const tone = good ? 'text-emerald-500' : bad ? 'text-red-500' : 'text-gray-400';
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '·';
  return (
    <div className="ds-card p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500">{title}</span>
        {pct !== null && (
          <span className={`text-xs font-medium ${tone}`}>
            {arrow} {Math.abs(pct)}%
          </span>
        )}
      </div>
      <div className={tone}>
        <Sparkline data={data} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ReviewCenter = () => {
  const navigate = useAppNavigate();
  const [data, setData] = useState<ReviewCenterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [areaFilter, setAreaFilter] = useState<string | null>(null);
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [busyRef, setBusyRef] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await reviewCenterService.get(days);
    setData(res);
    setLoading(false);
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const kpis = data?.headlineKpis;

  const filteredQueue = useMemo(() => {
    let q = data?.reviewQueue ?? [];
    if (areaFilter) q = q.filter((r) => r.stream === areaFilter);
    if (criticalOnly) q = q.filter((r) => r.severity === 'critical');
    return q;
  }, [data, areaFilter, criticalOnly]);

  const onAction = useCallback(
    async (row: QueueItem) => {
      if (row.action === 'resend' && row.ref) {
        setBusyRef(row.ref);
        const ok = await reviewCenterService.resendNotification(row.ref);
        setBusyRef(null);
        setBanner(ok ? 'Failed deliveries re-sent.' : 'Nothing was re-sent (no failed deliveries, or no permission).');
        window.setTimeout(() => setBanner(null), 3000);
        if (ok) void load();
        return;
      }
      navigate(routeFor(row));
    },
    [navigate, load],
  );

  const showTrends =
    !!data &&
    (data.trends.errorsPerDay.length > 0 ||
      data.trends.passRate.length > 0 ||
      data.trends.feedbackVolume.length > 0);

  return (
    <div className="p-4 md:p-6">
      <ScreenHeader
        icon="pi pi-verified"
        title="Review Center"
        subtitle={`System health, what needs attention, and where quality & spend are trending${data ? ` · updated ${relative(data.generatedAt)}` : ''}`}
        right={
          <>
            <Dropdown
              value={days}
              options={DAY_OPTIONS}
              onChange={(e) => setDays(e.value)}
              className="text-sm"
            />
            <button aria-label="Refresh"
              onClick={() => void load()}
              className="px-3 py-1.5 rounded-md text-sm border border-line text-ink-muted hover:bg-canvas transition-colors"
            >
              <i className={`pi pi-refresh ${loading ? 'pi-spin' : ''}`} />
            </button>
          </>
        }
      />

      {banner && (
        <div className="mb-3 px-3 py-2 rounded-md bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm">
          {banner}
        </div>
      )}

      {loading && !data ? (
        <PageSkeleton panels={2} className="" />
      ) : !data ? (
        <div className="text-center text-gray-400 py-16">Could not load the Review Center.</div>
      ) : (
        <>
          {/* Hero band */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="ds-card p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">System Health</div>
              <div className={`text-2xl font-bold ${SYSTEM_HEALTH_TONE[kpis!.systemHealth] || ''}`}>
                {kpis!.systemHealth}
              </div>
              <div className="text-xs text-gray-500">
                {kpis!.healthyAreas} of {kpis!.totalAreas} areas healthy
              </div>
            </div>

            <div className="ds-card p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Needs Attention</div>
              <div className="text-2xl font-bold text-ink">
                {kpis!.needsAttention}
              </div>
              <div className="text-xs text-gray-500">open items across all areas</div>
            </div>

            <div className="ds-card p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">AI Quality</div>
              <div className="text-2xl font-bold text-ink">
                {kpis!.aiQuality.passRate === null
                  ? '—'
                  : `${Math.round(kpis!.aiQuality.passRate * 100)}%`}
              </div>
              <div className="text-xs text-gray-500">
                {kpis!.aiQuality.deltaPts === null
                  ? 'pass rate'
                  : `${kpis!.aiQuality.deltaPts >= 0 ? '▲ +' : '▼ '}${kpis!.aiQuality.deltaPts} pts`}
              </div>
            </div>

            <div className="ds-card p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Spend ({data.windowDays}d)
              </div>
              <div className="text-2xl font-bold text-ink">
                {kpis!.spend.currency === 'USD' ? '$' : ''}
                {/* Sub-cent spend (e.g. $0.0004) would read as a flat $0.00 at 2 dp,
                    so show more precision for tiny non-zero amounts. */}
                {kpis!.spend.amount === 0
                  ? '0.00'
                  : kpis!.spend.amount < 0.01
                  ? kpis!.spend.amount.toFixed(4)
                  : kpis!.spend.amount.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500">
                {kpis!.spend.deltaPct === null
                  ? 'model + eval cost'
                  : `${kpis!.spend.deltaPct <= 0 ? '▼ ' : '▲ +'}${kpis!.spend.deltaPct}%`}
              </div>
            </div>
          </div>

          {/* Health by area */}
          <div className="mb-5">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
              Health by area <span className="normal-case">(click to filter)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.healthByArea.map((a) => {
                const active = areaFilter === a.area;
                return (
                  <button
                    key={a.area}
                    onClick={() => setAreaFilter(active ? null : a.area)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                      active
                        ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                        : 'border-line bg-surface'
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${AREA_DOT[a.state]}`} />
                    <span className="font-medium text-gray-700 dark:text-gray-200">{a.area}</span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500">{a.summary}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Review queue */}
          <div className="ds-card">
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <div className="font-semibold text-ink">What needs review</div>
              <div className="flex items-center gap-2 text-sm">
                {areaFilter && (
                  <button
                    onClick={() => setAreaFilter(null)}
                    className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                  >
                    {areaFilter} <i className="pi pi-times text-xs" />
                  </button>
                )}
                <button
                  onClick={() => setCriticalOnly((c) => !c)}
                  className={`px-2.5 py-1 rounded-md border ${
                    criticalOnly
                      ? 'bg-red-50 dark:bg-red-900/30 border-red-300 text-red-600 dark:text-red-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Critical
                </button>
              </div>
            </div>

            {filteredQueue.length === 0 ? (
              <div className="px-4 py-10 text-center text-gray-400">
                <i className="pi pi-check-circle text-2xl text-emerald-500" />
                <div className="mt-2 text-sm">Nothing to review right now.</div>
              </div>
            ) : (
              <ul>
                {filteredQueue.map((row, i) => (
                  <li
                    key={`${row.stream}-${row.ref}-${i}`}
                    className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-700/50 last:border-0"
                  >
                    <span className={`w-1.5 self-stretch rounded ${SEV_BAR[row.severity]}`} />
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {row.stream}
                    </span>
                    <span className="flex-1 text-sm text-ink truncate">
                      {row.title}
                    </span>
                    {row.ageMinutes !== null && (
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {ageLabel(row.ageMinutes)}
                      </span>
                    )}
                    <button
                      onClick={() => void onAction(row)}
                      disabled={busyRef === row.ref}
                      className="px-3 py-1 rounded-md text-sm border border-indigo-300 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 disabled:opacity-50 whitespace-nowrap"
                    >
                      {busyRef === row.ref ? (
                        <i className="pi pi-spin pi-spinner" />
                      ) : (
                        ACTION_LABEL[row.action]
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Trends */}
          {showTrends ? (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                Trends ({data.windowDays}d)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <TrendTile title="Errors / day" data={data.trends.errorsPerDay} goodDir="down" />
                <TrendTile title="AI pass-rate / run" data={data.trends.passRate} goodDir="up" />
                <TrendTile title="Feedback / day" data={data.trends.feedbackVolume} goodDir={null} />
              </div>
            </div>
          ) : (
            <div className="mt-4 text-xs text-gray-400">Not enough history yet for trends.</div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewCenter;
